import React, { MouseEvent, AnchorHTMLAttributes } from 'react';
import cn from 'classnames';

import OPE from 'js/on-page-editing';
import api from 'js/api-helper';
import { defineTheme, hasTheme, type ThemeKeys } from 'js/utils/theme-helpers';
import { split } from './link-helper';

import Icon, { type IconName } from 'components/icon/Icon';
import Image from 'components/image/Image';
import { ButtonTheme, buttonThemes } from 'components/button/Button';
import ExternalLink from './ExternalLink';

import ArrowIconSrc from 'images/arrow-rounded.svg';
import { ILink } from 'source/NFR.types';

export const linkThemes = defineTheme({
  actionLink: 'link--theme-action-link',
  extraIndentation: 'chapter-link--theme-extra-indentation',
  large: 'link--theme-large',
  previous: 'link--previous',
  next: 'link--next',
  navigationLink: 'navigation-link',
  navigationLinkNP: 'navigation-link-np',
  wrappedHeadingForPrint: 'wrapped-heading-for-print',

  // Legacy themes kept for backwards compatibility.
  blue: 'link--theme-blue',
  black: 'link--theme-black',
  bold: 'link--theme-bold',
  small: 'link--theme-small',
  tag: 'link--theme-tag',
  underline: 'chapter-link--theme-underline',
  portfolio: 'link--theme-portfolio',
  white: 'link--theme-white',
  fullWidth: 'link--theme-full-width'
});
export type LinkTheme = ThemeKeys<typeof linkThemes>;

// shared base props
type BaseProps = AnchorHTMLAttributes<HTMLAnchorElement> &
  ILink & {
    icon?: IconName;
    iconBeforeChildren?: IconName;
    iconClassName?: string;
    noHoverEffect?: boolean;
    useButtonStyles?: boolean;
    openInNewWindow?: boolean;
  };

// conditional prop typing: link vs button themes
export type LinkProps =
  | (BaseProps & {
      useButtonStyles?: false;
      theme?: LinkTheme | LinkTheme[];
    })
  | (BaseProps & {
      useButtonStyles: true;
      theme?: ButtonTheme | ButtonTheme[];
    });

// -----------------------------------------------------------------------------
// component
// -----------------------------------------------------------------------------

const Link = React.forwardRef<HTMLAnchorElement | HTMLSpanElement, LinkProps>(
  (
    {
      className,
      children,
      text,
      url,
      trackUrl,
      theme,
      useButtonStyles,
      onClick,
      onPageEditing,
      noHoverEffect,
      openInNewWindow,
      isExternal,
      isPrimary: _isPrimary, // This is only to add it to the CS model
      noFollow,
      icon,
      iconBeforeChildren,
      iconClassName,
      useSkinnyIcon,
      id,
      ...props
    },
    ref
  ) => {
    const isActionLink = !useButtonStyles && hasTheme(theme, 'actionLink');

    const handleOnClick = (e: MouseEvent<HTMLAnchorElement | HTMLSpanElement>) => {
      if (trackUrl) {
        const defaultBehaviour = e.ctrlKey;
        if (!defaultBehaviour) e.preventDefault();
        void api
          .execute(trackUrl, {})
          .catch(() => {})
          .finally(() => {
            if (!defaultBehaviour && url) window.location.href = url;
          });
      }
    };

    const baseClass = useButtonStyles ? 'button' : 'link';
    const newWindowProps = openInNewWindow ? { target: '_blank' } : {};
    const isExternalLink = isExternal && url;
    const isUntrusted = !!noFollow || !!openInNewWindow;
    const rel =
      [noFollow && 'nofollow', isUntrusted && 'noopener', isUntrusted && 'noreferrer'].filter(Boolean).join(' ') ||
      undefined;

    const sharedProps = {
      id,
      onClick: onClick || handleOnClick,
      role: onClick ? ('button' as const) : undefined,
      'aria-label': text,
      className: cn(
        baseClass,
        className,
        useButtonStyles ? buttonThemes(theme) : linkThemes(theme),
        useButtonStyles
          ? {
              'button--has-children': !!text || !!children,
              'button--has-icon-left': iconBeforeChildren,
              'button--has-icon-right': icon,
              'button--has-hover-effect': !noHoverEffect,
              'button--is-external-link': isExternalLink
            }
          : {
              'link--has-children': !!text || !!children,
              'link--has-icon-left': iconBeforeChildren,
              'link--has-icon-right': icon,
              'link--has-hover-effect': !noHoverEffect,
              'link--is-external-link': isExternalLink
            }
      ),
      ...OPE(onPageEditing),
      rel,
      ...newWindowProps,
      ...props
    };

    const content = (
      <>
        {isExternalLink ? (
          <ExternalLink
            text={text}
            iconClassName={iconClassName}
            iconBeforeChildren={iconBeforeChildren}
            icon={icon}
            useSkinnyIcon={useSkinnyIcon}
            isActionLink={isActionLink}
          >
            {children}
          </ExternalLink>
        ) : (
          <>
            {iconBeforeChildren && <Icon className={iconClassName} name={iconBeforeChildren} />}
            {isActionLink && (
              <div className="action-link-icon">
                <Image src={ArrowIconSrc} alt="ikon" isArrowIcon={true} width={24} height={24} isFluid={true} />
              </div>
            )}
            {children}
            {text && <span className="link--text">{split(text)}</span>}
            {icon && <Icon className={iconClassName} name={icon} />}
          </>
        )}
      </>
    );

    return url ? (
      <a {...sharedProps} href={url} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
        {content}
      </a>
    ) : (
      <span {...sharedProps} ref={ref as React.ForwardedRef<HTMLSpanElement>}>
        {content}
      </span>
    );
  }
);

Link.displayName = 'Link';

export default Link;
